import React, { useEffect, useState, useRef } from 'react';
import {
  Button,
  CircularProgress,
  Grid2,
  Typography,
  Box,
  Paper,
  Skeleton,
  alpha,
  useTheme,
  Stack,
  IconButton,
  Tooltip,
  Divider,
  createTheme,
  ThemeProvider,
  TextField,
} from '@mui/material';
import {
  Visibility,
  FilterList,
  PictureAsPdf,
  ZoomIn,
  ZoomOut,
  RestartAlt,
  Fullscreen,
  FullscreenExit,
  SearchOff,
  History,
} from '@mui/icons-material';
import MUIDataTable from 'mui-datatables';
import html2pdf from 'html2pdf.js/dist/html2pdf.bundle.min.js';
import { getAuditLog } from '../../../../helpers/api';
import { openNotification } from '../../../../helpers/notification';

export default function AuditLogs() {
  const theme = useTheme();
  const reportRef = useRef(null);

  // States
  const [logList, setLogList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasResult, setHasResult] = useState(true);
  const [zoom, setZoom] = useState(1);
  const [isFullWidth, setIsFullWidth] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    fetchLogList();
  }, []);

  const fetchLogList = async () => {
    setLoading(true);
    try {
      const res = await getAuditLog(startDate, endDate);
      const { data, status } = res?.result;
      if (status?.code === '00') {
        setLogList(data || []);
        setHasResult(data?.length > 0);
      } else {
        openNotification({ type: 'error', message: status?.desc });
      }
    } catch (error) {
      openNotification({
        type: 'error',
        message: 'Error fetching logs.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = () => {
    if (logList.length === 0) return;

    const element = reportRef.current;
    const opt = {
      margin: [10, 5, 10, 5],
      filename: `Audit_Report_${new Date().getTime()}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a3', orientation: 'landscape' },
    };

    html2pdf()
      .set(opt)
      .from(element)
      .save();
  };

  const columns = [
    {
      name: 'entryDate',
      label: 'Timestamp',
      options: {
        customBodyRender: (value) => (
          <Box>
            <Typography variant="body2">
              {new Date(value).toLocaleDateString('en-NG', { dateStyle: 'medium' })}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {new Date(value).toLocaleTimeString('en-NG', { hour12: true })}
            </Typography>
          </Box>
        ),
      },
    },
    {
      name: 'username',
      label: 'User',
      options: {
        customBodyRender: (value, tableMeta) => (
          <Box>
            <Typography variant="body2">{value}</Typography>
          </Box>
        ),
      },
    },
    {
      name: 'activity',
      label: 'Activity',
      options: {
        customBodyRender: (value) => (
          <Typography variant="body2" sx={{ color: '#2d3436', fontWeight: 500 }}>
            {value}
          </Typography>
        ),
      },
    },
    { name: 'division', label: 'Division' },
    {
      name: 'ipAddress',
      label: 'Connection Info',
      options: {
        customBodyRender: (value, tableMeta) => (
          <Box>
            <Typography
              variant="caption"
              sx={{ fontFamily: 'monospace', bgcolor: '#f1f2f6', px: 0.5, borderRadius: 1 }}
            >
              {value.replace('::ffff:', '')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {logList[tableMeta.rowIndex]?.browser.split('[')[0]}
            </Typography>
          </Box>
        ),
      },
    },
  ];

  const options = {
    filterType: 'none',
    responsive: 'standard',
    selectableRows: 'none',
    elevation: 0,
    rowsPerPage: 10,
    rowsPerPageOptions: [10, 25, 50, 100],
    download: false,
    filter: false,
    print: false,
    search: false,
    columns: false,
    textLabels: {
      body: { noMatch: loading ? <CircularProgress size={24} /> : 'No audit records found' },
    },
  };

  const renderContent = () => {
    if (loading && logList.length === 0) {
      return (
        <Box sx={{ p: 4 }}>
          <Skeleton variant="rectangular" width="100%" height={400} sx={{ borderRadius: 2 }} />
        </Box>
      );
    }

    if (!hasResult) {
      return (
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
          }}
        >
          <SearchOff sx={{ fontSize: 80, mb: 2, color: 'error.light', opacity: 0.5 }} />
          <Typography variant="h6" color="error" fontWeight={700}>
            No Logs Found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try refreshing the preview to fetch latest data.
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          height: '100%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#f5f5f5',
          overflow: 'hidden',
        }}
      >
        <Paper
          square
          elevation={1}
          sx={{
            p: 1,
            px: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            zIndex: 10,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Zoom Out">
              <IconButton size="small" onClick={() => setZoom((prev) => Math.max(prev - 0.1, 0.5))}>
                <ZoomOut />
              </IconButton>
            </Tooltip>
            <Typography
              variant="caption"
              sx={{ minWidth: 40, textAlign: 'center', fontWeight: 700 }}
            >
              {Math.round(zoom * 100)}%
            </Typography>
            <Tooltip title="Zoom In">
              <IconButton size="small" onClick={() => setZoom((prev) => Math.min(prev + 0.1, 1.5))}>
                <ZoomIn />
              </IconButton>
            </Tooltip>
            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
            <IconButton size="small" onClick={() => setIsFullWidth(!isFullWidth)}>
              {isFullWidth ? <FullscreenExit /> : <Fullscreen />}
            </IconButton>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button size="small" startIcon={<RestartAlt />} onClick={fetchLogList} color="inherit">
              Refresh
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<PictureAsPdf />}
              onClick={handleDownloadPDF}
            >
              Download PDF
            </Button>
          </Stack>
        </Paper>

        <Box
          sx={{
            flexGrow: 1,
            p: isFullWidth ? 0 : 3,
            overflow: 'auto',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Box
            ref={reportRef}
            sx={{
              width: '100%',
              maxWidth: isFullWidth ? '100%' : '1200px',
              transform: `scale(${zoom})`,
              transformOrigin: 'top center',
              transition: 'all 0.3s ease',
            }}
          >
            <MUIDataTable
              title={
                <Box py={3} px={0}>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: 'primary.main', letterSpacing: -1 }}
                  >
                    SYSTEM AUDIT TRAIL
                  </Typography>

                  {startDate && endDate && (
                    <Typography
                      variant="subtitle2"
                      sx={{ color: 'text.secondary', fontWeight: 600 }}
                    >
                      {new Date(startDate).toLocaleDateString('en-NG', { dateStyle: 'medium' })} -{' '}
                      {new Date(endDate).toLocaleDateString('en-NG', { dateStyle: 'medium' })}
                    </Typography>
                  )}
                </Box>
              }
              data={logList}
              columns={columns}
              options={options}
            />
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ height: '90vh', bgcolor: 'background.default', overflow: 'hidden' }}>
      <Grid2 container sx={{ height: '100%' }} spacing={0}>
        <Grid2 size={{ lg: 9, md: 8, sm: 12, xs: 12 }} sx={{ height: '100%' }}>
          {renderContent()}
        </Grid2>

        <Grid2
          size={{ lg: 3, md: 4, sm: 12, xs: 12 }}
          sx={{ height: '100%', bgcolor: 'white', borderLeft: 1, borderColor: 'divider' }}
        >
          <Stack sx={{ height: '100%' }}>
            <Box
              sx={{
                p: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <History color="primary" fontSize="small" />
                <Typography variant="subtitle2" fontWeight={700} color="primary.main">
                  REPORT CONTROLS
                </Typography>
              </Stack>
            </Box>
            <Box sx={{ p: 3, flexGrow: 1 }}>
              {/* <Typography variant="body2" color="text.secondary">
                The audit log contains the lastest system activities. Use the search bar in the
                table to filter results before downloading.
              </Typography> */}

              <Grid2 container spacing={2}>
                <Grid2 size={{ md: 12, xs: 12 }}>
                  <Typography>Start Date</Typography>
                  <TextField
                    // label="Start Date"
                    fullWidth
                    type="date"
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </Grid2>
                <Grid2 size={{ md: 12, xs: 12 }}>
                  <Typography>End Date</Typography>
                  <TextField
                    // label="End Date"
                    fullWidth
                    type="date"
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </Grid2>

                {/* <Grid2 size={{ md: 6, xs: 6 }}>
                  <Typography variant="caption" color="textSecondary">
                    Due Date
                  </Typography>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Due Date"
                      format="yyyy/MM/dd"
                      value={document.definition?.due_date}
                      onChange={(newValue) =>
                        handleUpdate('definition', {
                          ...document.definition,
                          due_date: newValue,
                        })
                      }
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'small',
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid2> */}
              </Grid2>
            </Box>

            <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={
                  loading ? <CircularProgress size={20} color="inherit" /> : <Visibility />
                }
                onClick={fetchLogList}
                disabled={loading}
                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
              >
                {loading ? 'Fetching...' : 'Fetch Logs'}
              </Button>
            </Box>
          </Stack>
        </Grid2>
      </Grid2>
    </Box>
  );
}
